import { FormField } from '@platform/shared/components/auto-form-dialog/decorators';
import type { FormFieldParams } from '@platform/shared/components/auto-form-dialog/types';
import { getFormNotations } from '@platform/shared/components/auto-form-dialog/helpers';
import { Column } from '@platform/shared/rpc/entity-decorators';
import type { ColumnParams, RpcTable } from '@platform/shared/rpc/types';
import { DataType } from '@platform/shared/rpc/types';
import { omitBy, pickBy } from 'lodash-es';
import { v4 as guid } from 'uuid';

import { Dao } from '@platform/entities/dao.class';

export type ReferenceForce = {
    value: string;
    label?: string | null;
    name?: string | null;
};
export type ReferenceClean = {
    // НЕ МУСОРИТЬ :( !
    value: string | null;
    label?: string | null;
    name?: string | null;
};
export type ReferenceItem<T = string> = {
    value: T | null;
    label: string | null;
    name: string | null;
    icon?: string | null;
    iconColor?: string | null;
};
export type Reference<T = string> = ReferenceItem<T> | T | null;

export type StrongReference = Exclude<Reference, string | null>;

export type ReferenceBoolean =
    | {
          value: boolean | null;
          label: string | null;
          name: string | null;
          icon?: string | null;
          iconColor?: string | null;
      }
    | boolean
    | null;

export type ReferenceNumber =
    | {
          value: number | null;
          label: string | null;
          name: string | null;
          icon?: string | null;
          iconColor?: string | null;
      }
    | number
    | null;

export type SysShared =
    | {
          is_child: boolean;
          sys_org_integration_id?: string;
          link?: string;
      }
    | string
    | null;

export type IStruct = Record<string, { dataType: DataType | [DataType] } & ColumnParams>;

export class Entity {
    public static _tableInfo: RpcTable;
    public static _struct: IStruct;
    public static _formFields: Record<
        string,
        FormFieldParams & { rules?: Array<string | { type: string; [key: string]: any }> }
    >;

    @Column(DataType.UUID)
    id?: Reference;

    client_grid_hierarchy_column?: string[];

    // @Column(DataType.BOOLEAN)
    // sys_is_viewed_doc?: boolean;

    @FormField({ skip: true })
    @Column(DataType.DATE, { writable: false })
    aud_when_create?: number;

    @FormField({ skip: true })
    @Column(DataType.VARCHAR)
    sys_row_status?: string;

    // @Column(DataType.VARCHAR)
    // is_group?: boolean;

    @FormField({ skip: true })
    @Column(DataType.DATE, { writable: false })
    aud_when_update?: number;

    constructor({ id }: { id?: string }) {
        this.id = id ?? guid();
    }

    static get tableRef() {
        return [this._tableInfo.schemaName, this._tableInfo.tableName].filter(Boolean).join('.');
    }

    static getDataStructure() {
        return {
            parentFieldName: null,
            schema: this._tableInfo?.schemaName,
            structure: Object.keys(this._struct).map((field) => ({ field, ...this._struct[field] })),
        };
    }

    static formQualifier<T>(item: EntityType<T>, props: any): () => Promise<{ default: any }> {
        return () => import('@platform/shared/components/auto-form-dialog/auto-form-dialog.vue');
    }

    static formLayoutQualifier<T>(item: EntityType<T>, props: any): () => Promise<{ default: any }> {
        return () => import('@platform/shared/components/auto-form-dialog/grid-skeleton.vue');
    }

    static linkedItemsDialogQualifier<T>(item: EntityType<T>, props: any): () => Promise<{ default: any }> {
        return () => import('@platform/shared/components/auto-form-dialog/grid-skeleton.vue');
    }

    static getFormNotations() {
        return getFormNotations(this);
    }

    static newDao() {
        return new Dao(this);
    }

    static pickStruct<E extends typeof Entity>(this: E, ...fields: (keyof InstanceType<E>)[]): IStruct {
        return pickBy(this._struct, (value, key) => fields.includes(key as keyof InstanceType<E>)) as IStruct;
    }

    static omitStruct<E extends typeof Entity>(this: E, ...fields: (keyof InstanceType<E>)[]): E {
        const newThis: E = Object.create(this);
        newThis._struct = omitBy(this._struct, (_, key) => fields.includes(key as keyof InstanceType<E>));
        return newThis;
    }

    static asView<E extends typeof Entity>(viewSchema: string, viewName: string): E {
        const newThis: E = Object.create(this);
        newThis._tableInfo = {
            ...newThis._tableInfo,
            schemaName: viewSchema,
            tableName: viewName,
        };
        return newThis;
    }

    static pickEntityWith<
        E extends typeof Entity,
        F extends keyof EntityType<InstanceType<E>> = keyof EntityType<InstanceType<E>>,
    >(this: E, ...fields: F[]): E {
        const newThis = Object.create(this);
        newThis._struct = this.pickStruct(...fields);
        return newThis;
    }

    static mixStruct<E extends typeof Entity>(
        this: E,
        struct: Record<string, undefined | null | ({ dataType: DataType | [DataType] } & ColumnParams)>
    ): E {
        const newThis: E = Object.create(this);
        newThis._struct = omitBy(
            {
                ...this._struct,
                ...(struct as typeof Entity._struct),
            },
            (value) => value == null
        );
        return newThis;
    }

    static asSystemTable<E extends typeof Entity>(this: E): E {
        const newThis = Object.create(this);
        newThis._tableInfo = { ...this._tableInfo, systemTable: true };
        return newThis;
    }
}

export type EntityType<T> = Pick<T, keyof T>;

export function ConnectDao<T extends Entity, R extends typeof Entity>(daoClass: new (entityClass: R) => Dao<T, R>) {
    return function (target: R) {
        (target as any).newDao = () => new daoClass(target);
    };
}
